import { I18n } from '@/i18n/I18n';
import { useState } from 'react';
import { Button, OverlayTrigger, Popover } from 'react-bootstrap';
import { CircleCheckBig, XCircle } from 'lucide-react';
import { EvidenceSubmission, ReviewerOutcome, SubmitterOutcome } from '@/backend/models/evidenceSubmission';
import { RequirementParams } from '@/backend/access/requirement/types';
import { useRequirementDocumentationStore } from '@/frontend/store/RequirementDocumentationStore';
import { useGlobalStore } from '@/frontend/store/GlobalStore';
import { EvidenceItemPreviewModal } from '../files/EvidenceItemPreviewModal';
import { ReviewerOutcomeModal } from './ReviewerOutcomeModal';

const REVIEWER_OUTCOME_BADGES = {
  [ReviewerOutcome.APPROVED]: { color: 'success', label: I18n.get('accepted') },
  [ReviewerOutcome.REJECTED]: { color: 'danger', label: I18n.get('rejected') },
};

interface EvidenceReviewSectionProps {
  submission: EvidenceSubmission;
  requirement: RequirementParams;
  allowReview: boolean;
}

export function EvidenceReviewSection({ submission, requirement, allowReview }: EvidenceReviewSectionProps) {
  const evidenceItem = useRequirementDocumentationStore((s) => s.evidenceItems.find((i) => i.id === submission.evidenceItemId));
  const userClass = useGlobalStore((s) => s.userClass);
  const [reviewModal, setReviewModal] = useState<{ outcome: ReviewerOutcome; label: string } | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const approveLabel = submission.submitterOutcome === SubmitterOutcome.DEVIATED ? I18n.get('approveDeviated') : I18n.get('accept');

  if (submission.reviewerOutcome) {
    return (
      <div className="mt-2">
        <div className="d-flex align-items-end gap-1">
          {submission.reviewerOutcome === ReviewerOutcome.APPROVED ? (
            <CircleCheckBig size={16} color="var(--green-600)" className="flex-shrink-0 mt-1" />
          ) : (
            <XCircle size={16} color="var(--red-600)" className="flex-shrink-0 mt-1" />
          )}
          <div className={`evidence-card-review-label ${submission.reviewerOutcome === ReviewerOutcome.APPROVED ? 'text-success' : 'text-danger'}`}>
            {REVIEWER_OUTCOME_BADGES[submission.reviewerOutcome].label}
          </div>
          {submission.reviewComment && (
            <OverlayTrigger
              trigger="click"
              placement="top"
              rootClose
              overlay={
                <Popover style={{ maxWidth: '300px' }}>
                  <Popover.Body className="text-wrap" style={{ whiteSpace: 'pre-wrap' }}>
                    {submission.reviewComment}
                  </Popover.Body>
                </Popover>
              }
            >
              <button type="button" className="btn btn-sm p-0" aria-label={I18n.get('reviewComment')}>
                <span className="list-card-meta link-primary">{I18n.get('viewComment')}</span>
              </button>
            </OverlayTrigger>
          )}
        </div>
        <p className="list-card-meta">{submission.reviewedBy}</p>
        <p className="evidence-card-review-date">
          {new Date(submission.reviewedAt!).toLocaleString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
        </p>
      </div>
    );
  }

  if (!allowReview || requirement.propelOrgId !== userClass?.getOrgId()) {
    return <span className="text-muted fst-italic small mt-2">{I18n.get('notYetReviewed')}</span>;
  }

  if (evidenceItem?.file.isPreviewable()) {
    return (
      <div className="d-flex gap-2 mt-2">
        <Button className="fusa-btn-small fusa-btn-primary" onClick={() => setShowPreview(true)}>
          {I18n.get('reviewEvidence')}
        </Button>
        {showPreview && (
          <EvidenceItemPreviewModal evidenceItem={evidenceItem} onHide={() => setShowPreview(false)} requirement={requirement} submission={submission} />
        )}
      </div>
    );
  }

  return (
    <div className="d-flex gap-2 mt-2">
      <Button className="fusa-btn-small fusa-btn-primary" onClick={() => setReviewModal({ outcome: ReviewerOutcome.APPROVED, label: approveLabel })}>
        {approveLabel}
      </Button>
      <Button className="fusa-btn-small btn-red-light" onClick={() => setReviewModal({ outcome: ReviewerOutcome.REJECTED, label: I18n.get('reject') })}>
        {I18n.get('reject')}
      </Button>
      {reviewModal && (
        <ReviewerOutcomeModal
          submissionId={submission.id}
          outcome={reviewModal.outcome}
          outcomeLabel={reviewModal.label}
          onHide={() => setReviewModal(null)}
        />
      )}
    </div>
  );
}
